// /var/www/afra/web-design.rampagesoft.com/my-app/src/metatag/MetaTagContentDetail.js
import { useEffect, useMemo } from 'react';
import { stripHtml, truncateText } from '../utility/utils';

const MetaTagContentDetail = ({ content, currentUrl, imageUrl }) => {
  const pageTitle =
    content?.Home_Sub &&
    content.Home_Sub !== 'กำลังโหลดชื่อเรื่อง...' &&
    content.Home_Sub !== 'กำลังโหลดชื่อเรื่องใหม่...' &&
    content.Home_Sub !== 'เกิดข้อผิดพลาด'
      ? stripHtml(content.Home_Sub)
      : 'Article Detail';

  const pageDescription = content?.Home_Short
    ? truncateText(stripHtml(content.Home_Short), 160)
    : 'ไม่มีคำอธิบาย';

  const publishedDate =
    content?.Home_Date && !isNaN(new Date(content.Home_Date).getTime())
      ? new Date(content.Home_Date).toISOString()
      : new Date().toISOString();

  const effectiveImageUrl = imageUrl || 'https://afaa.website/s/ff6970.png';
  const effectiveCurrentUrl = currentUrl || (typeof window !== 'undefined' ? window.location.href : '');

  const structuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: pageTitle,
      description: pageDescription,
      datePublished: publishedDate.split('T')[0],
      image: effectiveImageUrl,
      url: effectiveCurrentUrl,
      author: { '@type': 'Organization', name: 'AFRA APACHE' },
      publisher: {
        '@type': 'Organization',
        name: 'AFRA APACHE',
        logo: { '@type': 'ImageObject', url: 'https://afaa.website/s/ff6970.png' },
      },
    }),
    [pageTitle, pageDescription, publishedDate, effectiveImageUrl, effectiveCurrentUrl]
  );

  const sitemapSchema = useMemo(
    () => `
      <?xml version="1.0" encoding="UTF-8"?>
      <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
        <url>
          <loc>${effectiveCurrentUrl}</loc>
          <lastmod>${publishedDate.split('T')[0]}</lastmod>
          <changefreq>weekly</changefreq>
          <priority>0.8</priority>
        </url>
      </urlset>
    `,
    [effectiveCurrentUrl, publishedDate]
  );

  useEffect(() => {
    if (!content || !effectiveCurrentUrl || pageTitle === 'Article Detail' || !pageDescription) {
      return;
    }

    const head = document.head;
    const existingTagsQuery = '[data-meta-tag="content-detail"]';
    document.querySelectorAll(existingTagsQuery).forEach(tag => tag.remove());

    let titleTag = document.querySelector('title');
    if (!titleTag) {
      titleTag = document.createElement('title');
      head.appendChild(titleTag);
    }
    titleTag.textContent = pageTitle;
    titleTag.setAttribute('data-meta-tag', 'content-detail');

    const createTag = (tagName, attributes, textContent = null) => {
      const element = document.createElement(tagName);
      Object.entries(attributes).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === 'httpEquiv') {
          element.setAttribute('http-equiv', value);
        } else {
          element.setAttribute(key, value);
        }
      });
      if (textContent) element.textContent = textContent;
      element.setAttribute('data-meta-tag', 'content-detail');
      head.appendChild(element);
    };

    createTag('meta', { name: 'description', content: pageDescription });
    createTag('script', { type: 'application/ld+json' }, JSON.stringify(structuredData));

    const metaTagsConfig = [
      { tag: 'link', rel: 'icon', type: 'image/png', href: 'https://afaa.website/s/ff6970.png' },
      { tag: 'meta', httpEquiv: 'revisit-after', content: '3 days' },
      { tag: 'meta', httpEquiv: 'Cache-control', content: 'public' },
      { tag: 'meta', name: 'copyright', content: 'AFRA APACHE' },
      { tag: 'meta', name: 'author', content: 'AFRA APACHE' },
      { tag: 'link', rel: 'canonical', href: effectiveCurrentUrl },
      { tag: 'meta', property: 'og:title', content: pageTitle },
      { tag: 'meta', property: 'og:description', content: pageDescription },
      { tag: 'meta', property: 'og:type', content: 'article' },
      { tag: 'meta', property: 'og:url', content: effectiveCurrentUrl },
      { tag: 'meta', property: 'og:image', content: effectiveImageUrl },
      { tag: 'meta', property: 'og:site_name', content: 'web-design.rampagesoft.com' },
      { tag: 'meta', name: 'twitter:card', content: 'summary_large_image' },
      { tag: 'meta', name: 'twitter:title', content: pageTitle },
      { tag: 'meta', name: 'twitter:description', content: pageDescription },
      { tag: 'meta', name: 'twitter:image', content: effectiveImageUrl },
    ];

    metaTagsConfig.forEach(({ tag, ...attributes }) => createTag(tag, attributes));

    return () => {
      document.querySelectorAll(existingTagsQuery).forEach(tag => tag.remove());
    };
  }, [pageTitle, pageDescription, publishedDate, effectiveImageUrl, effectiveCurrentUrl, sitemapSchema, structuredData, content]);

  return null;
};

export default MetaTagContentDetail;